/**
 * PmsSettings — `/pms/settings`, la vista previa de la configuración V2.
 *
 *   PmsShell (banner de demo + sub-nav PMS)
 *     └── cabecera: propiedad + progreso de configuración
 *     └── escritorio: navegación lateral fija · contenido a la derecha
 *         móvil:      selector de sección (hoja) · contenido a pantalla completa
 *
 * La ruta de producción `/settings` no se toca: esta pantalla vive dentro del
 * espacio de nombres PMS y su estado es de sesión (`settingsV2Store`).
 *
 * La sección activa vive en `?s=<clave>` para que un enlace o una recarga
 * mantengan el sitio, sin inventar rutas nuevas por sección.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PmsShell from "@/pages/admin/pms/PmsShell";
import { acquireBodyScrollLock } from "@/lib/bodyScrollLock";
import { useMediaQuery, DESKTOP_QUERY } from "@/hooks/useMediaQuery";
import { useSettingsV2, completeness, unitLabelFor } from "@/lib/settingsV2Store";
import { SETTINGS_SECTIONS, SETTINGS_DEMO_BANNER } from "@/data/settingsV2Demo";
import {
  Buildings, House, CalendarCheck, IdentificationCard, Sparkle, Coins,
  UsersThree, ShieldCheck, ShareNetwork, Globe, BellSimple, PlugsConnected,
  CaretRight, Check, X,
} from "@phosphor-icons/react";

import SectionProperty from "@/pages/admin/pms/settings/SectionProperty";
import SectionUnits from "@/pages/admin/pms/settings/SectionUnits";
import SectionBookingRules from "@/pages/admin/pms/settings/SectionBookingRules";
import SectionGuestRequirements from "@/pages/admin/pms/settings/SectionGuestRequirements";
import SectionServices from "@/pages/admin/pms/settings/SectionServices";
import SectionFinance from "@/pages/admin/pms/settings/SectionFinance";
import SectionTeam from "@/pages/admin/pms/settings/SectionTeam";
import SectionRoles from "@/pages/admin/pms/settings/SectionRoles";
import SectionChannels from "@/pages/admin/pms/settings/SectionChannels";
import SectionWebsite from "@/pages/admin/pms/settings/SectionWebsite";
import SectionNotifications from "@/pages/admin/pms/settings/SectionNotifications";
import SectionIntegrations from "@/pages/admin/pms/settings/SectionIntegrations";

const ICONS = {
  Buildings, House, CalendarCheck, IdentificationCard, Sparkle, Coins,
  UsersThree, ShieldCheck, ShareNetwork, Globe, BellSimple, PlugsConnected,
};

const SECTION_COMPONENTS = {
  property: SectionProperty,
  units: SectionUnits,
  booking: SectionBookingRules,
  guests: SectionGuestRequirements,
  services: SectionServices,
  finance: SectionFinance,
  team: SectionTeam,
  roles: SectionRoles,
  channels: SectionChannels,
  website: SectionWebsite,
  notifications: SectionNotifications,
  integrations: SectionIntegrations,
};

const DEFAULT_SECTION = "property";

export default function PmsSettings() {
  const [params, setParams] = useSearchParams();
  const requested = params.get("s");
  const active = SECTION_COMPONENTS[requested] ? requested : DEFAULT_SECTION;

  const desktop = useMediaQuery(DESKTOP_QUERY);
  const settings = useSettingsV2();
  const progress = useMemo(() => completeness(settings), [settings]);
  const unitLabel = unitLabelFor(settings.property);

  const [sheetOpen, setSheetOpen] = useState(false);
  const contentRef = useRef(null);

  const goTo = (key) => {
    setParams({ s: key }, { replace: true });
    setSheetOpen(false);
    // Al cambiar de sección en móvil el usuario espera empezar arriba.
    if (contentRef.current && typeof contentRef.current.scrollIntoView === "function") {
      contentRef.current.scrollIntoView({ block: "start" });
    }
  };

  const Current = SECTION_COMPONENTS[active];
  const currentMeta = SETTINGS_SECTIONS.find((s) => s.key === active);

  return (
    <PmsShell>
      <SettingsHeader
        propertyName={settings.property.name}
        unitLabelPlural={unitLabel.plural}
        progress={progress}
      />

      <SavedHint tick={settings.saveTick} />

      <div className="lg:grid lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-10">
        {desktop ? (
          <DesktopNav active={active} onSelect={goTo} pending={progress.pending} />
        ) : (
          <MobileNav
            active={active}
            activeMeta={currentMeta}
            open={sheetOpen}
            onOpen={() => setSheetOpen(true)}
            onClose={() => setSheetOpen(false)}
            onSelect={goTo}
            pending={progress.pending}
          />
        )}

        <div
          ref={contentRef}
          className="min-w-0 scroll-mt-24"
          data-testid={`settings-section-${active}`}
          data-section={active}
        >
          <Current />
        </div>
      </div>
    </PmsShell>
  );
}

/* -------------------------------------------------------------------------
 * Cabecera
 * ---------------------------------------------------------------------- */

function SettingsHeader({ propertyName, unitLabelPlural, progress }) {
  return (
    <div className="mb-6 flex items-end justify-between gap-6 flex-wrap" data-testid="settings-header">
      <div className="min-w-0">
        <div className="text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-1">
          Configuración
        </div>
        <h1 className="font-serif-dv text-3xl sm:text-4xl text-stone-900 dark:text-stone-100 truncate">
          {propertyName}
        </h1>
        <p className="text-stone-500 dark:text-stone-400 text-sm mt-1">
          Configura la propiedad una vez y el resto del sistema se alimenta de aquí ·{" "}
          <span className="text-stone-400">{unitLabelPlural.toLowerCase()}</span>
        </p>
        <p
          className="text-xs text-stone-400 dark:text-stone-500 mt-1.5 max-w-xl"
          data-testid="settings-demo-scope"
        >
          {SETTINGS_DEMO_BANNER.subtitle}.
        </p>
      </div>
      <ProgressPill progress={progress} />
    </div>
  );
}

/** Indicador contenido: un porcentaje y un arco fino. Sin insignias ni retos. */
function ProgressPill({ progress }) {
  return (
    <div className="flex items-center gap-3" data-testid="settings-progress">
      <div className="relative w-11 h-11 shrink-0">
        <svg viewBox="0 0 36 36" className="w-11 h-11 -rotate-90" aria-hidden="true">
          <circle cx="18" cy="18" r="15.5" fill="none" strokeWidth="2.5"
                  className="stroke-stone-200 dark:stroke-stone-800" />
          <circle cx="18" cy="18" r="15.5" fill="none" strokeWidth="2.5" strokeLinecap="round"
                  className="stroke-stone-900 dark:stroke-stone-100"
                  strokeDasharray={`${(progress.pct / 100) * 97.4} 97.4`} />
        </svg>
      </div>
      <div className="leading-tight">
        <div className="text-lg text-stone-900 dark:text-stone-100 tabular-nums" data-testid="settings-progress-pct">
          {progress.pct}%
        </div>
        <div className="text-[11px] text-stone-500 dark:text-stone-400">configurado</div>
      </div>
    </div>
  );
}

/**
 * Aviso de guardado. Aparece tras cada cambio y se va solo. Deja claro que lo
 * guardado es la vista previa, nunca producción.
 */
function SavedHint({ tick }) {
  const [visible, setVisible] = useState(false);
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return undefined;
    }
    setVisible(true);
    const t = setTimeout(() => setVisible(false), 2200);
    return () => clearTimeout(t);
  }, [tick]);

  if (!visible) return null;
  return (
    <div
      className="fixed z-40 bottom-[calc(1rem_+_env(safe-area-inset-bottom,0px))] left-1/2 -translate-x-1/2 lg:left-auto lg:right-6 lg:translate-x-0 inline-flex items-center gap-2 text-xs text-stone-100 dark:text-stone-900 bg-stone-900/95 dark:bg-stone-100/95 rounded-full pl-2.5 pr-3.5 py-1.5 shadow-lg pointer-events-none"
      role="status"
      data-testid="settings-saved-hint"
    >
      <Check size={12} weight="bold" />
      Guardado en la vista previa
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Navegación
 * ---------------------------------------------------------------------- */

function NavItem({ section, active, onSelect, pendingCount, compact }) {
  const Icon = ICONS[section.icon] || Buildings;
  return (
    <button
      type="button"
      onClick={() => onSelect(section.key)}
      data-testid={`settings-nav-${section.key}`}
      aria-current={active ? "page" : undefined}
      className={`w-full text-left flex items-start gap-3 rounded-lg px-3 min-h-[44px] py-2.5 transition-colors ${
        active
          ? "bg-stone-100 dark:bg-stone-800 text-stone-900 dark:text-stone-100"
          : "text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-900"
      }`}
    >
      <Icon size={16} className="mt-0.5 shrink-0" weight={active ? "fill" : "regular"} />
      <span className="min-w-0 flex-1">
        <span className="block text-sm truncate">{section.label}</span>
        {!compact && (
          <span className="block text-[11px] text-stone-400 dark:text-stone-500 truncate">
            {section.hint}
          </span>
        )}
      </span>
      {pendingCount > 0 && (
        <span
          className="mt-1 shrink-0 w-1.5 h-1.5 rounded-full bg-stone-300 dark:bg-stone-600"
          title={`${pendingCount} por configurar`}
          data-testid={`settings-nav-${section.key}-pending`}
        />
      )}
    </button>
  );
}

function DesktopNav({ active, onSelect, pending }) {
  return (
    <nav
      className="hidden lg:block self-start sticky top-24 space-y-0.5"
      aria-label="Secciones de configuración"
      data-testid="settings-nav-desktop"
    >
      {SETTINGS_SECTIONS.map((s) => (
        <NavItem
          key={s.key}
          section={s}
          active={s.key === active}
          onSelect={onSelect}
          pendingCount={pending[s.key] || 0}
        />
      ))}
    </nav>
  );
}

/**
 * Móvil: nada de barra lateral encogida. Un botón ancho abre una hoja
 * inferior con las 12 secciones, cada una con altura de dedo y respetando el
 * área segura de iOS.
 */
function MobileNav({ active, activeMeta, open, onOpen, onClose, onSelect, pending }) {
  const Icon = ICONS[activeMeta?.icon] || Buildings;

  // Misma cortesía que SidePanel: Escape cierra y el fondo no hace scroll.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const release = acquireBodyScrollLock();
    return () => {
      document.removeEventListener("keydown", onKey);
      release();
    };
  }, [open, onClose]);

  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        data-testid="settings-nav-mobile-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        className="lg:hidden w-full mb-5 dv-card flex items-center gap-3 min-h-[56px] text-left"
      >
        <Icon size={18} weight="fill" className="shrink-0 text-stone-700 dark:text-stone-300" />
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] tracking-[0.2em] uppercase text-stone-400">Sección</span>
          <span className="block text-sm text-stone-900 dark:text-stone-100 truncate">
            {activeMeta?.label}
          </span>
        </span>
        <CaretRight size={16} className="shrink-0 text-stone-400" />
      </button>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" data-testid="settings-nav-sheet">
          <div
            className="absolute inset-0 bg-stone-900/40 backdrop-blur-[2px]"
            onClick={onClose}
            data-testid="settings-nav-sheet-backdrop"
          />
          <div
            className="absolute inset-x-0 bottom-0 max-h-[82vh] overflow-y-auto rounded-t-2xl bg-white dark:bg-stone-950 border-t border-stone-200 dark:border-stone-800 pb-[calc(1rem_+_env(safe-area-inset-bottom,0px))]"
          >
            <div className="sticky top-0 bg-white dark:bg-stone-950 px-4 pt-4 pb-3 flex items-center justify-between border-b border-stone-100 dark:border-stone-800">
              <div className="font-serif-dv text-lg">Ajustes</div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                data-testid="settings-nav-sheet-close"
                className="w-11 h-11 -mr-2 flex items-center justify-center text-stone-500"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-2">
              {SETTINGS_SECTIONS.map((s) => (
                <NavItem
                  key={s.key}
                  section={s}
                  active={s.key === active}
                  onSelect={onSelect}
                  pendingCount={pending[s.key] || 0}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
